// Default jurisdiction profiles. The Canadian profile mirrors the Figma
// onboarding copy (PCMLTFA / PIPEDA, provinces, Quebec gating); the EU profile
// matches the current backend reality (EUR, IBAN, GDPR/AMLD). Both are data —
// edit here (or, later, in the back office) to change consents, validation,
// currency, allowed documents, or blocked regions without code changes.
//
// Note: the Figma mock labelled the AML act "FCMLTFA"; the correct Canadian
// statute is the PCMLTFA (Proceeds of Crime (Money Laundering) and Terrorist
// Financing Act), used here.

import type { JurisdictionProfile } from '../schema.js';

import { allowedResidenceCountries } from './residence-countries.js';

const CONSENT_VERSION = '2026-01';

export const canadaProfile: JurisdictionProfile = {
  id: 'CA',
  displayName: 'Canada',
  locale: 'en-CA',
  currency: 'CAD',
  accountIdentifier: 'ACCOUNT_NUMBER',
  amlRegulation: { actName: 'PCMLTFA', retentionYears: 5 },
  privacyRegulation: { actName: 'PIPEDA' },
  phone: { defaultCountry: 'CA', defaultDialCode: '+1' },
  identityDocuments: ['PASSPORT', 'NATIONAL_ID'],
  address: {
    countryDefault: 'CA',
    countryOptions: allowedResidenceCountries,
    fields: [
      { id: 'streetAddress', label: 'Street address', required: true, placeholder: '1055 W Georgia Street, Suite 1500' },
      { id: 'city', label: 'City', required: true },
      { id: 'province', label: 'Province', required: true },
      { id: 'postalCode', label: 'Postal code', required: true, pattern: '^[A-Za-z]\\d[A-Za-z] ?\\d[A-Za-z]\\d$' },
    ],
  },
  consents: [
    {
      id: 'terms',
      required: true,
      version: CONSENT_VERSION,
      label: 'I accept the NORVIX Customer Terms and Privacy Policy.',
    },
    {
      id: 'identity-checks',
      required: true,
      version: CONSENT_VERSION,
      label:
        'I authorize NORVIX to verify my identity and run sanctions, PEP and adverse-media checks under the PCMLTFA. Verification records are retained 5 years after the relationship ends.',
    },
    {
      id: 'privacy-notice',
      required: true,
      version: CONSENT_VERSION,
      label:
        'I acknowledge the NORVIX Privacy Notice, the purposes for which my personal information is collected, and my right to access or correct it under PIPEDA.',
    },
    {
      id: 'marketing',
      required: false,
      version: CONSENT_VERSION,
      label: 'Send me product updates and offers from NORVIX. I can unsubscribe at any time.',
    },
  ],
  blockedSubregions: [
    { code: 'CA-QC', label: 'Quebec', reason: 'NORVIX is not yet available to residents of Quebec.' },
  ],
};

export const euProfile: JurisdictionProfile = {
  id: 'EU',
  displayName: 'European Union',
  locale: 'en-IE',
  currency: 'EUR',
  accountIdentifier: 'IBAN',
  amlRegulation: { actName: 'AMLD5', retentionYears: 5 },
  privacyRegulation: { actName: 'GDPR' },
  phone: { defaultCountry: 'LT', defaultDialCode: '+370' },
  identityDocuments: ['PASSPORT', 'NATIONAL_ID'],
  address: {
    countryDefault: 'LT',
    countryOptions: allowedResidenceCountries,
    fields: [
      { id: 'streetAddress', label: 'Street address', required: true },
      { id: 'city', label: 'City', required: true },
      { id: 'region', label: 'Region', required: false },
      { id: 'postalCode', label: 'Postal code', required: true, pattern: '^[A-Za-z0-9 -]{3,10}$' },
    ],
  },
  consents: [
    {
      id: 'terms',
      required: true,
      version: CONSENT_VERSION,
      label: 'I accept the NORVIX Customer Terms and Privacy Policy.',
    },
    {
      id: 'identity-checks',
      required: true,
      version: CONSENT_VERSION,
      label:
        'I authorise NORVIX to verify my identity and run sanctions, PEP and adverse-media checks under AMLD5. Verification records are retained 5 years after the relationship ends.',
    },
    {
      id: 'privacy-notice',
      required: true,
      version: CONSENT_VERSION,
      label:
        'I acknowledge the NORVIX Privacy Notice and my rights under the GDPR, including access, rectification and erasure of my personal data.',
    },
    {
      id: 'marketing',
      required: false,
      version: CONSENT_VERSION,
      label: 'Send me product updates and offers from NORVIX. I can unsubscribe at any time.',
    },
  ],
  blockedSubregions: [],
};

/** Country (ISO-3166 alpha-2) → jurisdiction id. */
export const countryJurisdiction: Record<string, string> = Object.fromEntries(
  allowedResidenceCountries.map(({ code }) => [code, code === 'CA' ? 'CA' : 'EU']),
);
